/* eslint react/no-unknown-property: "off" */
import React from 'react';
import PropTypes from 'prop-types';
import Zone from '../models/zone';
import { undefined_or_null } from '../utils/tools';
export function Polygon({
    points,
    style = null,
    hoverStyle = null,
    onClick = () => {},
    isHovered = false,
    onHover = () => {},
    className = '',
    ...rest
}) {
    const polygon_style = React.useMemo(() => {
        let polygon_style = style ? { ...style } : {};
        if (!isHovered) {
            return polygon_style;
        }
        const hover_style = hoverStyle || {};
        return { ...style, ...hover_style };
    }, [isHovered, style, hoverStyle]);

    return (
        <polygon
            points={points}
            style={polygon_style}
            onClick={onClick}
            onMouseOver={() => onHover(true)}
            onMouseLeave={() => onHover(false)}
            className={`polygon ${className}`}
            {...rest}
        />
    );
}
Polygon.propTypes = {
    points: PropTypes.string,
    style: PropTypes.object,
    hoverStyle: PropTypes.object,
    onClick: PropTypes.func,
    isHovered: PropTypes.bool,
    onHover: PropTypes.func,
    rest: PropTypes.array,
    className: PropTypes.string,
};

export function ZonePolygons({
    zone,
    onZoneSelected = () => {},
    onZoneOver = () => {},
    defaultColor = '#0032FA',
}) {
    const [isHovered, setIsHovered] = React.useState(false);

    const handleHover = React.useCallback(
        (isHovered = false) => {
            setIsHovered(isHovered);
            if (isHovered) {
                return onZoneOver(zone);
            }
            return onZoneOver(null);
        },
        [onZoneOver],
    );

    const { style, hover_style } = React.useMemo(() => {
    // compute default unavailable style
        if (!zone.isAvailable) {
            return {
                style: {
                    fill: zone.seatlib_data.unavailable_color || 'lightgray',
                    stroke: zone.seatlib_data.unavailable_stroke || 'lightgray',
                    strokeWidth: !undefined_or_null(
                        zone.seatlib_data.unavailable_stroke_width,
                    )
                        ? zone.seatlib_data.unavailable_stroke_width
                        : '5px',
                    opacity: !undefined_or_null(zone.seatlib_data.unavailable_opacity)
                        ? zone.seatlib_data.unavailable_opacity
                        : 0.6,
                },
                hover_style: {
                    fill: zone.seatlib_data.unavailable_hover_color || 'gray',
                    stroke: zone.seatlib_data.unavailable_hover_stroke || 'gray',
                    strokeWidth: !undefined_or_null(
                        zone.seatlib_data.unavailable_hover_stroke_width,
                    )
                        ? zone.seatlib_data.unavailable_hover_stroke_width
                        : '5px',
                    opacity: !undefined_or_null(
                        zone.seatlib_data.unavailable_hover_opacity,
                    )
                        ? zone.seatlib_data.unavailable_hover_opacity
                        : 1,
                },
            };
        }

        // First we compute style from seatlib_data
        // If not, we pick in colors attribute if exists (array of colors, we take the first element)
        // Otherwise default color (weez blue by default)

        return {
            style: {
                fill:
          zone.seatlib_data.color ||
          (zone.colors && zone.colors.length ? zone.colors[0] : defaultColor),
                stroke:
          zone.seatlib_data.stroke ||
          (zone.colors && zone.colors.length ? zone.colors[0] : defaultColor),
                strokeWidth: !undefined_or_null(zone.seatlib_data.stroke_width)
                    ? zone.seatlib_data.stroke_width
                    : '5px',
                opacity: !undefined_or_null(zone.seatlib_data.opacity)
                    ? zone.seatlib_data.opacity
                    : 0.6,
            },
            hover_style: {
                fill:
          zone.seatlib_data.hover_color ||
          (zone.colors && zone.colors.length ? zone.colors[0] : defaultColor),
                stroke:
          zone.seatlib_data.hover_stroke ||
          (zone.colors && zone.colors.length ? zone.colors[0] : defaultColor),
                strokeWidth: !undefined_or_null(zone.seatlib_data.hover_stroke_width)
                    ? zone.seatlib_data.hover_stroke_width
                    : '5px',
                opacity: !undefined_or_null(zone.seatlib_data.hover_opacity)
                    ? zone.seatlib_data.hover_opacity
                    : 1,
            },
        };
    }, [zone]);

    if (!zone.seatlib_data || !zone.seatlib_data.points) {
        return null;
    }

    return zone.seatlib_data.points.map((point_array, i) => (
        <Polygon
            key={i}
            points={point_array.map((p) => p.s).join(' ')}
            style={style}
            hoverStyle={hover_style}
            isHovered={isHovered}
            onHover={handleHover}
            onClick={() => onZoneSelected(zone)}
            className={`zone-polygon zone-polygon-${zone.uuid}${isHovered ? ' zone-polygon-hover' : ''}${zone.isAvailable ? ' zone-polygon-available' : ' zone-polygon-unavailable'}`}
            data-zone-id={zone.uuid}
        />
    ));
}

ZonePolygons.propTypes = {
    zone: PropTypes.instanceOf(Zone).isRequired,
    onZoneSelected: PropTypes.func,
    onZoneOver: PropTypes.func,
    defaultColor: PropTypes.string,
};

export const SVG = React.forwardRef(
    (
        {
            image,
            onLoad = () => {},
            children,
            onClick,
            onDoubleClick,
            className = '',
            onMouseMove,
            onEscapeDown = () => {},
            ...rest
        },
        ref,
    ) => {
        const [img, setImage] = React.useState(null);

        React.useEffect(() => {
            let img = new window.Image();
            img.src = image;
            img.addEventListener('load', () => {
                setImage(img);
            });
        }, [image]);

        const { width, height } = React.useMemo(() => {
            if (!img) {
                return {};
            }
            return { width: img.width, height: img.height };
        }, [img]);

        React.useEffect(() => {
            document.addEventListener('keydown', onEscapeDown, false);
            return () => {
                document.removeEventListener('keydown', onEscapeDown, false);
            };
        }, []);

        if (!img) {
            return null;
        }

        return (
            <svg
                ref={ref}
                xmlns="http://www.w3.org/2000/svg"
                className={className}
                onLoad={onLoad}
                viewBox={`0 0 ${width} ${height}`}
                onClick={onClick}
                onDoubleClick={onDoubleClick}
                onMouseMove={onMouseMove}
                {...rest}
            >
                <defs>
                    <filter id="gaussian-blur-svg">
                        <feColorMatrix type="saturate" values="0.10" result="GRAY" />
                        <feImage id="feimage" xlinkHref="#mask" result="MASK" />
                        <feComposite in="GRAY" in2="MASK" operator="in" result="COMP" />
                        <feMerge result="MERGE">
                            <feMergeNode in="SourceGraphic" />
                            <feMergeNode in="COMP" />
                        </feMerge>
                    </filter>
                </defs>
                <image
                    x={0}
                    y={0}
                    href={image}
                    height="100%"
                    width="100%"
                    className="filtered"
                />
                {children}
            </svg>
        );
    },
);

SVG.displayName = 'SVG';

SVG.propTypes = {
    image: PropTypes.string,
    onLoad: PropTypes.func,
    children: PropTypes.node,
    onClick: PropTypes.func,
    onDoubleClick: PropTypes.func,
    className: PropTypes.string,
    onMouseMove: PropTypes.func,
    onEscapeDown: PropTypes.func,
};

export function ZoneEditor({
    zone,
    onDoubleClick = () => {},
    onClickPolygon = () => {},
    onLoad = () => {},
    onClick = () => {},
    defaultColor = '#0032FA',
    allowDrawing = true,
}) {
    const svg = React.useRef(null);
    const [current_path, setCurrentPath] = React.useState([]);
    const [current_point, setCurrentPoint] = React.useState(null);

    const get_svg_coords = React.useCallback((x, y) => {
        const pt = svg.current.createSVGPoint();
        pt.x = x;
        pt.y = y;
        return pt.matrixTransform(svg.current.getScreenCTM().inverse());
    }, []);

    const handleClick = React.useCallback(
        (e) => {
            if (!allowDrawing) {
                return;
            }
            const { clientX, clientY } = e;
            const coords = get_svg_coords(clientX, clientY);
            const { x, y } = coords;
            const tmp_current_path = [
                ...current_path,
                { x: parseInt(x), y: parseInt(y) },
            ];
            setCurrentPath(tmp_current_path);
            setCurrentPoint(null);
            onClick(e, tmp_current_path);
        },
        [current_path, allowDrawing],
    );

    const handleDoubleClick = React.useCallback(() => {
        const points = Object.values(
            current_path.reduce((acc, { x, y }) => {
                let obj = {
                    x,
                    y,
                    s: `${x},${y}`,
                };
                acc[obj.s] = obj;
                return acc;
            }, {}),
        );
        setCurrentPath([]);
        setCurrentPoint(null);
        onDoubleClick(points);
    }, [current_path, onDoubleClick]);

    const handleMouseMove = React.useCallback(
        (e) => {
            const { clientX, clientY } = e;
            const coords = get_svg_coords(clientX, clientY);
            const { x, y } = coords;
            const tmp_current_path = [...current_path];
            const tmp_current_point = { x: parseInt(x), y: parseInt(y) };
            if (current_point) {
                tmp_current_path.pop();
            }
            tmp_current_path.push(tmp_current_point);
            setCurrentPoint(tmp_current_point);
            setCurrentPath(tmp_current_path);
        },
        [current_path, current_point],
    );

    const handleEscapeDown = React.useCallback((e) => {
        if (e.key === 'Escape') {
            return setCurrentPath([]);
        }
    }, []);

    const zone_model = React.useMemo(() => {
        return new Zone(zone);
    }, [zone]);

    const current_path_svg = React.useMemo(() => {
        if (current_path.length < 2) {
            return [];
        }
        const lines = [];
        for (let i = 0; i < current_path.length - 1; i++) {
            lines.push(
                `${current_path[i].x},${current_path[i].y} ${current_path[i + 1].x},${current_path[i + 1].y}`,
            );
        }
        return lines;
    }, [current_path]);

    const current_path_style = React.useMemo(() => {
        return {
            stroke: defaultColor,
            strokeWidth: '5px',
        };
    }, [defaultColor]);

    if (!zone.preview_picture) {
        return null;
    }

    return (
        <SVG
            image={zone.preview_picture.url}
            onClick={handleClick}
            onDoubleClick={handleDoubleClick}
            ref={svg}
            className={'throne-zone-editor'}
            onLoad={onLoad}
            onMouseMove={handleMouseMove}
            onEscapeDown={handleEscapeDown}
        >
            {zone_model.getChildren().map((child, i) => (
                <ZonePolygons
                    key={i}
                    zone={child}
                    onZoneSelected={onClickPolygon}
                    defaultColor={defaultColor}
                />
            ))}
            {current_path_svg.map((points, i) => (
                <Polygon
                    key={`polygon_${i}`}
                    points={points}
                    style={current_path_style}
                    className="polygon_in_progress"
                />
            ))}
        </SVG>
    );
}

ZoneEditor.propTypes = {
    zone: PropTypes.object.isRequired,
    onClickPolygon: PropTypes.func.isRequired,
    onDoubleClick: PropTypes.func,
    onLoad: PropTypes.func,
    onClick: PropTypes.func,
    defaultColor: PropTypes.string,
    allowDrawing: PropTypes.bool,
};
